import React, { useMemo, useState } from "react";

import { PineScriptCodeBlock } from "../Reports/PineScriptCodeBlock";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useReportStrategyPineRequirements } from "../../hooks/useReportStrategyPineRequirements";
import { coerceUxCopy } from "../../utils/pineIrUi";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "./StrategyPineSectionCard";

const FALLBACK_STRATEGY_TEMPLATE = `//@version=5
strategy("My strategy", overlay=true)
// ---------- INPUTS ----------
// your inputs
// ---------- LOGIC ----------
long_condition = ...
short_condition = ...
// ---------- EXECUTION ----------
if long_condition and strategy.position_size == 0
    strategy.entry("Long", strategy.long, qty=1)
if short_condition and strategy.position_size == 0
    strategy.entry("Short", strategy.short, qty=1)`;

const FALLBACK_INDICATOR_TEMPLATE = `//@version=5
indicator("My indicator", overlay=true)
// ---------- INPUTS ----------
// your inputs
// ---------- LOGIC ----------
signal_long = ...
signal_short = ...
// ---------- OUTPUT ----------
plot(signal_long ? 1 : 0, title="Long signal")
plot(signal_short ? -1 : 0, title="Short signal")`;

const TEMPLATE_CODE_MAX_H = "280px";

type TemplateMode = "strategy" | "indicator";

function templateModeTitle(mode: TemplateMode): string {
  return `What we expect for a ${mode} (merge-friendly)`;
}

function templateModeSummary(mode: TemplateMode, summaryFromApi: string | null): string {
  if (summaryFromApi) return summaryFromApi;
  if (mode === "indicator") {
    return "Structure Tessera can parse for indicator workflows: version header, indicator() declaration, and clear plotted/derived signal outputs.";
  }
  return "Structure Tessera can merge: version header, strategy() declaration, entries inside if guards with position_size == 0.";
}

export function TesseraFriendlyStructurePanel(): React.ReactElement {
  const { data: requirements } = useReportStrategyPineRequirements();
  const [mode, setMode] = useState<TemplateMode>("strategy");
  const templates = requirements?.supported_structure?.templates;
  const hasTabbedTemplates = Boolean(templates?.strategy || templates?.indicator);
  const strategyTemplate =
    templates?.strategy?.trim() || requirements?.supported_structure?.template?.trim() || FALLBACK_STRATEGY_TEMPLATE;
  const indicatorTemplate = templates?.indicator?.trim() || FALLBACK_INDICATOR_TEMPLATE;
  const template = useMemo(
    () => (mode === "indicator" ? indicatorTemplate : strategyTemplate),
    [indicatorTemplate, mode, strategyTemplate]
  );
  const summaryFromApi = coerceUxCopy(requirements?.supported_structure?.summary);
  const title = templateModeTitle(mode);
  const summary = templateModeSummary(mode, summaryFromApi);

  const codeBlock = (
    <PineScriptCodeBlock
      code={template}
      maxH={TEMPLATE_CODE_MAX_H}
      brandChrome
      chromeSubtitle="Example template"
    />
  );

  return (
    <Field data-tessera-friendly-structure="" className="gap-3">
      <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{title}</FieldLabel>
      <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>{summary}</FieldDescription>

      {hasTabbedTemplates ? (
        <Tabs
          data-trade-log-tabs=""
          data-tessera-friendly-structure-tabs=""
          value={mode}
          onValueChange={(value) => setMode(value as TemplateMode)}
          className="w-full min-w-0"
        >
          <div className="w-full overflow-x-auto overflow-y-hidden">
            <TabsList className="min-w-max">
              <TabsTrigger value="strategy">Strategy</TabsTrigger>
              <TabsTrigger value="indicator">Indicator</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="strategy" className="mt-0 min-w-0 outline-none">
            <PineScriptCodeBlock
              code={strategyTemplate}
              maxH={TEMPLATE_CODE_MAX_H}
              brandChrome
              chromeSubtitle="Example template"
            />
          </TabsContent>

          <TabsContent value="indicator" className="mt-0 min-w-0 outline-none">
            <PineScriptCodeBlock
              code={indicatorTemplate}
              maxH={TEMPLATE_CODE_MAX_H}
              brandChrome
              chromeSubtitle="Example template"
            />
          </TabsContent>
        </Tabs>
      ) : (
        codeBlock
      )}
    </Field>
  );
}
